import { compactApprovalTitle } from "./approvalHistoryPresentation";
import { AttachmentChip } from "../files/AttachmentChip";
import type {
  ChatAttachment,
  EnvironmentId,
  ScopedThreadRef,
  UserInputAttachmentAnswerPayload,
} from "@amoreg/contracts";
import { getQuestionAnswerText } from "@amoreg/client-runtime/work-log/user-input";
import { MessageCircleQuestionIcon } from "lucide-react";
import { useAssetUrlState } from "../../assets/assetUrls";
import { type RightPanelAttachment, useRightPanelStore } from "../../rightPanelStore";
import { MarkdownImage } from "./MarkdownImage";

function AnswerImage({
  attachment,
  environmentId,
}: {
  attachment: ChatAttachment;
  environmentId: EnvironmentId;
}) {
  const asset = useAssetUrlState(environmentId, {
    _tag: "attachment",
    attachmentId: attachment.id,
    fileName: attachment.name,
  });
  if (asset._tag !== "Success")
    return (
      <span className="text-xs text-muted-foreground">
        {attachment.name}
        {asset._tag === "Failure" ? " (unavailable)" : ""}
      </span>
    );
  if (attachment.type === "image")
    return (
      <MarkdownImage
        src={asset.url}
        alt={attachment.name}
        width={asset.imageDimensions?.width}
        height={asset.imageDimensions?.height}
        className="max-h-40 max-w-full rounded-md object-contain"
      />
    );
  return null;
}

/** Keep each question and its submitted answer visible outside collapsed work. */
export function QuestionAnswerHistory({
  answer,
  environmentId,
  threadRef,
}: {
  answer: UserInputAttachmentAnswerPayload;
  environmentId?: EnvironmentId | undefined;
  /** With a conversation to open into, file attachments open in the right panel. */
  threadRef?: ScopedThreadRef | null | undefined;
}) {
  const openInPanel = threadRef
    ? (attachment: RightPanelAttachment, assetEnvironmentId: EnvironmentId) =>
        useRightPanelStore
          .getState()
          .openAttachment(threadRef, attachment, {
            kind: "asset",
            environmentId: assetEnvironmentId,
          })
    : null;
  const ids = [
    ...new Set([
      ...Object.keys(answer.questionTextById ?? {}),
      ...Object.keys(answer.answers),
      ...Object.keys(answer.attachmentsByQuestionId),
    ]),
  ];
  return (
    <section
      aria-label="Question and answer"
      className="my-2 space-y-3 rounded-lg border border-border/70 p-3 text-sm"
    >
      {ids.map((id) => (
        <div key={id} className="space-y-2">
          <p className="flex items-start gap-2 font-medium">
            <MessageCircleQuestionIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
            {compactApprovalTitle(
              answer.questionSummaryById?.[id] ?? answer.questionTextById?.[id] ?? "Question",
              answer.questionDetailsById?.[id],
            )}
          </p>
          {(answer.questionLinesById?.[id]?.length ?? 0) > 0 ? (
            <ul className="space-y-1 pl-6 text-secondary-label">
              {answer.questionLinesById?.[id]?.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          ) : null}
          {answer.questionDetailsById?.[id] ? (
            <details className="pl-6 text-xs text-muted-foreground">
              <summary className="w-fit cursor-pointer rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                {answer.questionLinesById?.[id] ? "Details" : "View request details"}
              </summary>
              <pre
                className={
                  answer.questionLinesById?.[id]
                    ? "mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted/30 p-2 font-sans text-xs"
                    : "mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted/30 p-2 font-mono text-[11px]"
                }
              >
                {answer.questionDetailsById[id]}
              </pre>
            </details>
          ) : null}
          {getQuestionAnswerText(answer.answers[id]) ? (
            <p className="whitespace-pre-wrap pl-6 text-secondary-label">
              {getQuestionAnswerText(answer.answers[id])}
            </p>
          ) : null}
          {(answer.attachmentsByQuestionId[id]?.length ?? 0) > 0 ? (
            <ul className="flex flex-wrap gap-2 pl-6" aria-label="Answer attachments">
              {answer.attachmentsByQuestionId[id]?.map((attachment) => (
                <li key={attachment.id}>
                  {environmentId ? (
                    attachment.type === "file" ? (
                      <AttachmentChip
                        attachment={attachment}
                        environmentId={environmentId}
                        {...(openInPanel
                          ? { onOpen: () => openInPanel(attachment, environmentId) }
                          : {})}
                      />
                    ) : (
                      <AnswerImage attachment={attachment} environmentId={environmentId} />
                    )
                  ) : (
                    attachment.name
                  )}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ))}
    </section>
  );
}
